import { Component, Input, OnInit } from '@angular/core';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { forkJoin, from, of, throwError } from 'rxjs';
import { catchError, concatMap, tap, toArray } from 'rxjs/operators';
import { IApiCompany, IApiInvestigation, IApiSageClass } from 'src/app/shared/modules/graphql/types/types';
import { LoaderService } from 'src/app/shared/modules/loader/loader.service';
import { CompanyService } from 'src/app/shared/services';

/** A Bill To company that has to be linked to a SAGE class */
export interface ISageClassTarget {
  company: IApiCompany;
}

/** What the dialog hands back: the companies that were linked, and whether every one of them was */
export interface ISageClassSyncResult {
  complete: boolean;
  companies: IApiCompany[];
}

interface ISageClassRow extends ISageClassTarget {
  sageClasses: IApiSageClass[];
  selectedSageId: string;
  status: 'pending' | 'saving' | 'done' | 'error';
  error?: string;
  loadError?: string;
  result?: IApiCompany;
}

@Component({
  selector: 'app-sage-class-selection-modal',
  templateUrl: './sage-class-selection-modal.component.html',
  styleUrls: ['./sage-class-selection-modal.component.scss']
})
export class SageClassSelectionModalComponent extends DialogContentBase implements OnInit {
  @Input() investigation: IApiInvestigation;
  @Input() targets: ISageClassTarget[] = [];

  public rows: ISageClassRow[] = [];
  public isSaving = false;

  constructor(
    public dialogRef: DialogRef,
    private companyService: CompanyService,
    private loader: LoaderService
  ) {
    super(dialogRef);
  }

  /** Every company needs a choice, unless it was already linked */
  public get canSave(): boolean {
    return !this.isSaving && this.rows.some(({ status }) => status !== 'done')
      && this.rows.every(({ status, selectedSageId }) => status === 'done' || !!selectedSageId);
  }

  ngOnInit(): void {
    this.rows = this.targets.map((target) => ({ ...target, sageClasses: [], selectedSageId: '', status: 'pending' }));

    this.loader.show$(forkJoin(this.rows.map((row) =>
      this.companyService.getSageClassesByCompanyId(row.company.id).pipe(
        // a failed search still leaves "create new" as a choice
        catchError((error) => {
          row.loadError = error?.message || 'The SAGE classes could not be loaded.';
          return of([]);
        })
      )
    ))).subscribe((lists) => lists.forEach((sageClasses, index) => this.rows[index].sageClasses = sageClasses));
  }

  /** Hands back whatever was linked, even when the dialog is closed half way, so the caller can pick up from there */
  close() {
    const companies = this.rows.filter(({ status, result }) => status === 'done' && result).map(({ result }) => result);
    this.dialogRef.close({
      complete: companies.length === this.rows.length,
      companies
    } as ISageClassSyncResult);
  }

  submit() {
    this.isSaving = true;

    // one at a time, SAGE hands out class ids from the latest id
    from(this.rows.filter(({ status }) => status !== 'done')).pipe(
      concatMap((row) => this.sync(row)),
      toArray()
    ).subscribe({
      next: () => {
        this.isSaving = false;
        this.close();
      },
      // the failed row shows its message and the ones after it stay pending, saving again carries on from there
      error: () => this.isSaving = false
    });
  }

  private sync(row: ISageClassRow) {
    row.status = 'saving';
    row.error = null;

    const request = row.selectedSageId === 'NEW'
      ? this.companyService.createSageClass(row.company.id)
      : this.companyService.update({
        id: row.company.id,
        sageKey: row.sageClasses.find(({ id }) => id === row.selectedSageId)?.key || '',
        name: row.company.name,
      });

    return request.pipe(
      tap((response) => {
        if (!response?.sageKey) throw new Error('SAGE did not link this class. Please try again.');
        row.status = 'done';
        row.result = response;
      }),
      catchError((error) => {
        row.status = 'error';
        row.error = error?.message || 'The class could not be synced with SAGE.';
        return throwError(error);
      })
    );
  }
}
